import React, { FC, ReactNode, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useSwipeable } from 'react-swipeable';

import { useButton, useDeviceInfo, useI18n, useLogger, useSelector, useStaffCan } from '~Hooks';
import { PaymentMethod } from '~reducers';

import Icon from '~ComponentsImproved/Icon';
import { Box, Button, Dimmer, GrayButton } from '~Styles';
import GiftCardIcon from '~Assets/icons/redeem.svg';
import CashIcon from '~Assets/icons/money.svg';
import InvoiceIcon from '~Assets/icons/receipt.svg';
import PrintedInvoiceIcon from '~Assets/icons/receipt_long.svg';
import SwishIcon from '~Assets/icons/swish.svg';
import VippsIcon from '~Assets/icons/vipps.svg';
import TimeoutIcon from '~Assets/icons/schedule.svg';
import PaymentsIcon from '~Assets/icons/payments.svg';
import ZettleIcon from '~Assets/icons/payment.svg';
import SmallTerminalIcon from '~Assets/icons/calculator.svg';
import GeneralPaymentIcon from '~Assets/icons/account_balance_wallet.svg';
import CryptoIcon from '~Assets/icons/currency_bitcoin.svg';
import { useStorage } from '~Components/Storage';
import PayWithIcon from '~/Assets/icons/more_horiz.svg';
import CloseIcon from '~/Assets/icons/close.svg';
import Modal from '~ComponentsImproved/Modal';
import { ModalContent } from '~Styles/modal';
import Heading from '~ComponentsImproved/Typography/Heading';
import AdyenTerminalButton from '~Scenes/Tables/Components/PayButtons/AdyenTerminalButton';

const ListWrapper = styled.div`
  width: 270px;
  height: 0;
  position: absolute;
  right: 0;
`;

const Anchor = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
`;

const Container = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 4px;
  background-color: var(--colorWhite);
  bottom: 4px;
  max-height: calc(100vh - 230px);
  z-index: 3;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const SquareButton = styled(Button)<{
  methodsOpen: boolean;
  fullWidth: boolean;
  whiteButton?: boolean;
}>`
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '64px')};
  height: 64px;
  z-index: ${({ methodsOpen }) => (methodsOpen ? 5 : 2)};
  position: relative;
  color: ${({ methodsOpen, whiteButton }) =>
    methodsOpen
      ? 'var(--colorStatusRed)'
      : whiteButton
      ? 'var(--colorBlueGray1300)'
      : 'var(--colorWhite)'};
  background: ${({ methodsOpen, whiteButton }) =>
    methodsOpen
      ? 'var(--colorWhite)'
      : whiteButton
      ? 'var(--colorWhite)'
      : 'var(--colorGradientGreen)'};
`;

const List = styled.div`
  overflow: auto;
`;

const ListItemWrapper = styled.div`
  height: 64px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  color: var(--colorBlueGray900);
  font-weight: 600;
  font-size: 14px;
  letter-spacing: var(--letterSpacing);
  gap: 8px;
  justify-content: space-between;

  button {
    padding-top: 4px;
    padding-bottom: 4px;
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--colorBlueGray200);
  }

  div {
    margin-right: 16px;
  }
`;

const ListItem = styled.div`
  height: 64px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  color: var(--colorBlueGray900);
  font-weight: 600;
  font-size: 14px;
  letter-spacing: var(--letterSpacing);

  &:not(:last-child) {
    border-bottom: 1px solid var(--colorBlueGray200);
  }

  div {
    margin-right: 16px;
  }
`;

const AdyenTerminalWrapper = styled(ModalContent)`
  max-width: 400px;
`;

const methodToIconMap: Record<string, string> = {
  ADYEN: PaymentsIcon,
  ADYEN_MANUELL: PaymentsIcon,
  ADYEN_PAY_AT_TABLE: PaymentsIcon,
  PRESENTKORT: GiftCardIcon,
  GIFTCARD: GiftCardIcon,
  BAEMINGO_GIFTCARD: GiftCardIcon,
  MICRODEB: GiftCardIcon,
  RETAIN24: GiftCardIcon,
  CASH: CashIcon,
  INVOICE: InvoiceIcon,
  IZETTLE: ZettleIcon,
  PRINTED_INVOICE: PrintedInvoiceIcon,
  SURFBOARD: SmallTerminalIcon,
  SWISH: SwishIcon,
  SWISH_QR: SwishIcon,
  TIMEOUT: TimeoutIcon,
  TIMEOUT_TIP: TimeoutIcon,
  TIMEOUT_FAIL: TimeoutIcon,
  CRYPTO: CryptoIcon,
  VIPPS: VippsIcon,
  TARJETA_DE_CREDITO: PaymentsIcon,
};

interface Props {
  paidAmount?: number; // Used to exclude INVOICE as a payment method if there is a partial payment
  onPay: (methodId: string, poiid?: string) => void;
  splitPaymentButton?: ReactNode;
  fullButtonText?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  whiteButton?: boolean;
}

const PayWithButton: FC<Props> = ({
  paidAmount,
  onPay,
  splitPaymentButton,
  fullButtonText,
  style,
  disabled,
  whiteButton = false,
}) => {
  const { i18n } = useI18n();
  const staffCan = useStaffCan();
  const deviceInfo = useDeviceInfo();
  const venueId = useSelector((state) => state.venueId);
  const [storage] = useStorage();
  const logger = useLogger('pay-with-buttons');

  const listRef = useRef(null as null | HTMLDivElement);
  const [touching, setTouching] = useState<boolean>(false);
  const [methodsOpen, setMethodsOpen] = useState<boolean>(false);
  const [showTerminalSelection, setShowTerminalSelection] = useState<boolean>(false);

  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const filteredPaymentMethods = paymentMethods.filter(
    (pm) => pm.venues.some((v) => v === venueId) || pm.venues.length === 0,
  );
  const busyTerminals = useSelector((state) => state.POSPayments)
    .filter((p) => p.variant && p.status === 'PENDING')
    .map((p) => p.variant);

  const primaryPaymentMethod = filteredPaymentMethods.find((pm) => pm.isPrimary);
  const hasAdyen = filteredPaymentMethods.some((method) => method.id === 'ADYEN');
  const hasZettle = filteredPaymentMethods.some((method) => method.id === 'IZETTLE');

  // Check if there is a default payment method available
  const defaultPaymentMethod =
    deviceInfo?.specificPaymentMethod || primaryPaymentMethod?.id || hasAdyen || hasZettle;

  // Sort methods A-Z for consistency
  const filteredMethods = useSelector((state) =>
    state.POSPaymentMethods.filter((p) => {
      // Exclude Adyen and iZettle as they are automatically assigned
      if (!primaryPaymentMethod && ['ADYEN', 'IZETTLE'].includes(p.id)) {
        return false;
      }

      if (defaultPaymentMethod && p.id === defaultPaymentMethod) {
        return false;
      }

      if (!staffCan.canUsePaymentMethod(p.id)) {
        return false;
      }

      // Exclude CASH if disabled in storage
      if (storage.disableCash && p.id === 'CASH') {
        return false;
      }

      // Partial payments with INVOICE are not compatible with accounting flows
      if (paidAmount && paidAmount > 0 && p.id === 'INVOICE') {
        return false;
      }

      return true;
    }),
  );

  const sortedMethods = [...filteredMethods].sort((a, b) => a.title.localeCompare(b.title));

  const handlers = useSwipeable({
    delta: 10,
    onSwiping: () => setTouching(false),
  });

  const handleMethodTouch = (method: PaymentMethod) => {
    // Check if it really was a touch, and not a swipe etc
    if (touching) {
      storage.adyenTerminals.length > 1 && method.id === 'ADYEN'
        ? setShowTerminalSelection(true)
        : onPay(method.id);
    }
  };

  const adyenSelectCancelButton = useButton({
    onClick: () => {
      setShowTerminalSelection(false);
    },
  });

  const button = useButton({
    onClick: () => setMethodsOpen((prev) => !prev),
    disabled: disabled || !staffCan.MAKE_PAYMENT,
    disabledToast: !staffCan.MAKE_PAYMENT ? i18n('Staff.NoPermission.MakePayment') : undefined,
  });

  return (
    <>
      {methodsOpen && (
        <ListWrapper>
          <Dimmer
            as="span"
            active
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMethodsOpen(false);
            }}
          />

          <Anchor>
            <Container ref={listRef}>
              <List {...handlers}>
                {splitPaymentButton && <ListItemWrapper>{splitPaymentButton}</ListItemWrapper>}
                {sortedMethods.map((method) => (
                  <ListItem
                    key={method.id}
                    onTouchStart={() => setTouching(true)}
                    onTouchEnd={() => handleMethodTouch(method)}>
                    <Icon
                      color="var(--colorBlueGray600)"
                      src={methodToIconMap[method.id] || GeneralPaymentIcon}
                    />{' '}
                    {method.title}
                  </ListItem>
                ))}
              </List>
            </Container>
          </Anchor>
        </ListWrapper>
      )}

      {paymentMethods.length > 0 && (
        <SquareButton
          {...button}
          style={style}
          whiteButton={whiteButton}
          fullWidth={!defaultPaymentMethod || Boolean(fullButtonText)}
          methodsOpen={methodsOpen}>
          {defaultPaymentMethod && !fullButtonText ? (
            <Icon
              src={methodsOpen ? CloseIcon : PayWithIcon}
              width="24px"
              height="24px"
              color={methodsOpen ? 'var(--colorStatusRed)' : 'white'}
            />
          ) : (
            <>{methodsOpen ? i18n('General.Cancel') : i18n('POS.PayWith')}</>
          )}
        </SquareButton>
      )}

      <Modal
        isShowing={showTerminalSelection}
        hide={() => setShowTerminalSelection(false)}
        backdropClose>
        <AdyenTerminalWrapper>
          <Heading level={2} bottom={16} color="#fff">
            {i18n('Payment.SelectTerminal')}
          </Heading>
          <Box display="flex" flexDirection="column" gap="xs">
            {storage.adyenTerminals.map((terminal) => (
              <AdyenTerminalButton
                key={terminal.POIID}
                poiid={terminal.POIID}
                handleSetupPayment={(poiid) => onPay('ADYEN', poiid)}
                closeSelector={() => setShowTerminalSelection(false)}
                isbusy={terminal.POIID ? busyTerminals.includes(terminal.POIID) : false}
              />
            ))}
            <GrayButton {...adyenSelectCancelButton} fluid size="large">
              {i18n('General.Cancel')}
            </GrayButton>
          </Box>
        </AdyenTerminalWrapper>
      </Modal>
    </>
  );
};

export default PayWithButton;
